import React, { useState } from 'react';
import { Container, Typography, CircularProgress, Box, Alert, Button, Stack } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faFilePdf, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useDashboard } from '../hooks/useDashboard';
import dashboardService from '../services/dashboardService';
import { saveBlobAsFile } from '../utils/downloadFile';
import KPIStats from '../components/dashboard/KPIStats';
import SalesTrendChart from '../components/dashboard/SalesTrendChart';
import OrderStatusChart from '../components/dashboard/OrderStatusChart';
import TopProductsTable from '../components/dashboard/TopProductsTable';
import LowStockTable from '../components/dashboard/LowStockTable';
import SalesFilter from '../components/salesAnalytics/SalesFilter';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../context/WarehouseContext';
import { toISTDateString } from '../utils/dates';
import analyticsCache from '../utils/analyticsCache';

const Dashboard = () => {
  const { t } = useTranslation();
  const { getDashboardData } = useDashboard();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const { selectedWarehouseIds } = useWarehouseContext();

  const [period, setPeriod] = useState('last_30_days');

  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      from_date: toISTDateString(startOfMonth),
      to_date: toISTDateString(endOfMonth),
    };
  });

  const fetchingRef = React.useRef(false);

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
  };

  const fetchData = React.useCallback(async (force = false) => {
    // For custom period, require dates
    if (period === 'custom' && (!dateRange.from_date || !dateRange.to_date)) return;

    // Prevent double fetch
    if (fetchingRef.current) return;

    const warehouseKey = selectedWarehouseIds?.length > 0 ? `_w${selectedWarehouseIds.join('_')}` : '';
    const cacheKey = period === 'custom'
      ? `dashboard_data_custom_${dateRange.from_date}_${dateRange.to_date}${warehouseKey}`
      : `dashboard_data_${period}${warehouseKey}`;
    const cachedData = analyticsCache.getItem(cacheKey);

    if (!force && cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        if (parsed) {
          setData(parsed);
          return;
        }
      } catch (e) {
        console.error("Failed to parse cached dashboard data", e);
        analyticsCache.removeItem(cacheKey);
      }
    }

    fetchingRef.current = true;
    setLoading(true);
    setFetchError(null);

    const params = { period };

    // Only include dates for custom period
    if (period === 'custom') {
      params.from_date = dateRange.from_date;
      params.to_date = dateRange.to_date;
    }

    try {
      const result = await getDashboardData(params);

      if (result.success) {
        setData(result.data);
        // Guard the cache write: once browser storage fills (the analytics pages
        // cache a blob per period×warehouse and never evict), setItem throws
        // QuotaExceededError. Previously that skipped the loading reset below and
        // left the dashboard spinning forever. The cache is a nicety — swallow it.
        try {
          analyticsCache.setItem(cacheKey, JSON.stringify(result.data));
        } catch (e) {
          /* storage full / unavailable — render from state, skip caching */
        }
      } else {
        setFetchError(result.error);
      }
    } finally {
      // Always clear loading + the in-flight guard, even if the fetch throws or a
      // storage write fails — otherwise the page is stuck and can't refetch.
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [period, dateRange, getDashboardData, selectedWarehouseIds]);

  // Initial fetch on mount
  React.useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouseIds]);

  const [exporting, setExporting] = useState(null); // 'excel' | 'pdf' | null

  /**
   * Download the dashboard for exactly the period on screen, so the file matches what
   * the user is looking at. Dates are sent only for a custom period, mirroring fetchData.
   *
   * Note: these exports deliberately omit money and stock figures (backend policy) even
   * though the live dashboard above shows them — the file is not a copy of the screen.
   */
  const handleExport = async (format) => {
    setExporting(format);
    setFetchError(null);
    try {
      const params = { period };
      if (period === 'custom') {
        params.from_date = dateRange.from_date;
        params.to_date = dateRange.to_date;
      }
      const result = await dashboardService.exportDashboard(format, params);
      if (result.success) {
        saveBlobAsFile(result.data, result.headers, `dashboard.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      } else {
        setFetchError(result.error || t('common.alerts.errorMessage'));
      }
    } finally {
      setExporting(null);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={5} display="flex" justifyContent="space-between" alignItems="flex-end">
        <div>
          <Typography
            variant="h3"
            component="h1"
            fontWeight="800"
            sx={{
              background: 'linear-gradient(45deg, #05445E 30%, #189AB4 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            {t('dashboard.title')}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 500 }}>
            {t('dashboard.subtitle')}
          </Typography>
        </div>
        <Box textAlign="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end" mb={data ? 1.5 : 0}>
            <Button
              size="small"
              variant="outlined"
              disabled={!!exporting}
              onClick={() => handleExport('excel')}
              startIcon={
                <FontAwesomeIcon
                  icon={exporting === 'excel' ? faSpinner : faFileExcel}
                  className={exporting === 'excel' ? 'fa-spin' : ''}
                />
              }
            >
              {t('dashboard.exportExcel')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!!exporting}
              onClick={() => handleExport('pdf')}
              startIcon={
                <FontAwesomeIcon
                  icon={exporting === 'pdf' ? faSpinner : faFilePdf}
                  className={exporting === 'pdf' ? 'fa-spin' : ''}
                />
              }
            >
              {t('dashboard.exportPdf')}
            </Button>
          </Stack>
          {data && (
            <>
              <Typography variant="caption" display="block" color="textSecondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                {t('dashboard.generatedAt')}
              </Typography>
              <Typography variant="body2" fontWeight="600" color="primary">
                {data.generated_at ? new Date(data.generated_at).toLocaleString() : '—'}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Date Filter */}
      <SalesFilter
        fromDate={dateRange.from_date}
        toDate={dateRange.to_date}
        onFromDateChange={(val) => handleDateChange('from_date', val)}
        onToDateChange={(val) => handleDateChange('to_date', val)}
        onSubmit={() => fetchData(true)}
        loading={loading}
        period={period}
        onPeriodChange={handlePeriodChange}
      />

      {fetchError && (
        <Alert severity="error" sx={{ mb: 3 }}>{fetchError || 'Failed to load dashboard data'}</Alert>
      )}

      {loading && !data && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      )}

      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {/* KPI Stats */}
          <KPIStats kpis={data.kpis} />

          {/* Charts Row */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 420px', minWidth: 320 }}>
              <SalesTrendChart data={data.sales_trend} />
            </div>
            <div style={{ flex: '1 1 320px', minWidth: 280 }}>
              <OrderStatusChart data={data.order_status} />
            </div>
          </div>

          {/* Tables Row */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 420px', minWidth: 320 }}>
              <TopProductsTable products={data.top_products} />
            </div>
            <div style={{ flex: '1 1 420px', minWidth: 320 }}>
              <LowStockTable products={data.low_stock_products} />
            </div>
          </div>
        </div>
      )}
    </Container>
  );

};

export default Dashboard;